import { CoreButton, CoreInput, CoreModalDialog, CoreModalDialogFooter, CoreSelect, MMButton } from '@components'
import SuccessIcon from '@images/icons/success-check.svg?react'
import cn from 'classnames'
import { useCreateTicketWithMessagesMutation, useGetTicketCategoriesQuery } from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useEffect, useRef, useState } from 'react'
import {
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENT_SIZE_MB,
  MAX_INITIAL_MESSAGES,
  MAX_MESSAGE_LENGTH,
  MAX_TOTAL_MESSAGE_LENGTH,
  validateFiles
} from '../../utils'
import ComposeArea, { type ComposeAreaHandle } from '../ComposeArea/ComposeArea'
import { buildTicketWithMessagesPayload, countMessageSlots, type MessageDraft } from './buildTicketWithMessagesPayload'

import styles from './CreateTicketModal.module.scss'

interface Props {
  visible: boolean
  onSuccess: (ticketId: number) => void
  onClose: () => void
  onCloseAfterSuccess?: (ticketId: number) => void
  initialTitle?: string
  initialCategoryDescription?: string
}

function CreateTicketModal({
  visible,
  onSuccess,
  onClose,
  onCloseAfterSuccess,
  initialTitle,
  initialCategoryDescription
}: Props) {
  const [submitted, setSubmitted] = useState(false)
  const [submittedId, setSubmittedId] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [sentMessages, setSentMessages] = useState<MessageDraft[]>([])
  const [composeText, setComposeText] = useState('')
  const [composeFiles, setComposeFiles] = useState<File[]>([])
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const composeRef = useRef<ComposeAreaHandle>(null)

  const [createTicketWithMessages, { isLoading: createFetching }] = useCreateTicketWithMessagesMutation()
  const { data: categories } = useGetTicketCategoriesQuery(undefined, { skip: !visible })

  const categoryOptions = (categories ?? []).map(cat => ({ value: cat.id, label: cat.description }))
  const committedMessageSlots = countMessageSlots(sentMessages)
  const totalMessageCharacters = sentMessages.reduce((sum, msg) => sum + msg.text.length, 0) + composeText.length

  const hasUnsavedContent =
    title.trim() || categoryId !== '' || sentMessages.length > 0 || composeText.trim() || composeFiles.length > 0

  const canSubmit =
    title.trim() !== '' &&
    categoryId !== '' &&
    sentMessages.length > 0 &&
    composeText.trim() === '' &&
    composeFiles.length === 0
  const canAddMessage = composeText.trim() !== '' || composeFiles.length > 0

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on hide, apply prefill only when the modal opens
  useEffect(() => {
    if (!visible) {
      resetForm()
      return
    }
    if (initialTitle) setTitle(initialTitle)
  }, [visible])

  // biome-ignore lint/correctness/useExhaustiveDependencies: preselect the category once it has loaded from the API
  useEffect(() => {
    if (!visible || !initialCategoryDescription || categoryId !== '') return
    const match = categories?.find(cat => cat.description === initialCategoryDescription)
    if (match?.id != null) setCategoryId(match.id)
  }, [visible, categories])

  function handleClose() {
    if (hasUnsavedContent) {
      if (
        !window.confirm(
          tr(
            'FRONTEND.SUPPORT.CREATE.DISCARD_CONFIRM',
            'Are you sure you want to discard your request? Your changes will be lost.'
          )
        )
      ) {
        return
      }
    }
    onClose()
  }

  function handleSuccessClose() {
    if (submittedId != null && onCloseAfterSuccess) onCloseAfterSuccess(submittedId)
    else onClose()
  }

  function handleComposeKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && e.shiftKey) return
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddMessage()
    }
  }

  function handleAddMessage() {
    if (!canAddMessage) return
    if (composeText.length > MAX_MESSAGE_LENGTH) return
    const draftSlots = composeFiles.length > 0 ? composeFiles.length : 1
    if (committedMessageSlots + draftSlots > MAX_INITIAL_MESSAGES) return
    setSentMessages(prev => [...prev, { text: composeText, files: composeFiles }])
    setComposeText('')
    setComposeFiles([])
    setFileErrors([])
  }

  function addFiles(files: File[]) {
    const { valid, errors } = validateFiles(files)
    const allErrors = [...errors]
    const currentTotal = composeFiles.reduce((sum, f) => sum + f.size, 0)
    const newTotal = valid.reduce((sum, f) => sum + f.size, 0)
    if (currentTotal + newTotal > MAX_ATTACHMENT_SIZE_BYTES) {
      allErrors.push(
        tr('FRONTEND.SUPPORT.ERROR.TOTAL_SIZE', 'Total attachments exceed the {{MAX_ATTACHMENT_SIZE_MB}}MB limit', {
          MAX_ATTACHMENT_SIZE_MB
        })
      )
      setFileErrors(allErrors)
      return
    }
    const draftSlots = composeFiles.length + valid.length
    if (committedMessageSlots + draftSlots > MAX_INITIAL_MESSAGES) {
      allErrors.push(
        tr(
          'FRONTEND.SUPPORT.ERROR.TOO_MANY_MESSAGES',
          'Adding these files would exceed the {{MAX_INITIAL_MESSAGES}}-message limit',
          {
            MAX_INITIAL_MESSAGES
          }
        )
      )
      setFileErrors(allErrors)
      return
    }
    setFileErrors(allErrors)
    if (valid.length > 0) setComposeFiles(prev => [...prev, ...valid])
  }

  function removeComposeFile(index: number) {
    setComposeFiles(prev => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit() {
    if (!canSubmit) return
    setSubmitError(null)
    try {
      // canSubmit already requires an empty compose box, so handleSubmit only ever runs with
      // committed drafts -- no in-progress draft can be included here.
      const { messages, fileMessageMapping, files } = buildTicketWithMessagesPayload(sentMessages)

      const ticket = await createTicketWithMessages({
        ticket: {
          title: title.trim(),
          categoryId: Number(categoryId),
          messages,
          fileMessageMapping
        },
        files
      }).unwrap()

      setSubmittedId(ticket.id as number)
      setSubmitted(true)
    } catch (err: unknown) {
      const e = err as {
        error?: string
        statusCode?: number
        status?: number
        cause?: { field?: string; cause?: string }[]
      }
      const httpStatus = e?.statusCode ?? e?.status
      if (httpStatus === 429) {
        setSubmitError(
          tr(
            'FRONTEND.SUPPORT.ERROR.RATE_LIMIT_CREATE',
            "You've reached the daily limit of 10 support requests. Please try again tomorrow."
          )
        )
      } else {
        // `||`, not `??`, is intentional: an empty-string error from BE should also fall through
        // to the fallback text, not render a blank ErrorDisplay. (mmfintech-backend-api's error
        // shape never includes a `data` property -- that branch was dead code, removed.)
        const message =
          e?.error || tr('FRONTEND.SUPPORT.ERROR.CREATE_FAILED', 'Something went wrong while submitting your request.')
        // Submitting N messages that all fail the same validation rule (e.g. disallowed
        // characters) returns N near-identical `cause` entries, one per message index -- dedupe
        // by the cause text itself, and drop the field/JSON-path prefix (e.g.
        // "messages[0].message"), which is an internal detail and not useful to the user.
        const causeDetail = Array.from(
          new Set((Array.isArray(e?.cause) ? e.cause : []).map(c => c.cause).filter((c): c is string => Boolean(c)))
        ).join('; ')
        setSubmitError(causeDetail ? `${message} (${causeDetail})` : message)
      }
    }
  }

  function resetForm() {
    setSubmitted(false)
    setSubmittedId(null)
    setTitle('')
    setCategoryId('')
    setSentMessages([])
    setComposeText('')
    setComposeFiles([])
    setFileErrors([])
    setDragActive(false)
    setSubmitError(null)
  }

  const isSuccess = submitted && submittedId != null

  const successContent = (
    <div className={cn(styles['ctm-success'])}>
      <div className={cn(styles['ctm-success-icon'])}>
        <SuccessIcon />
      </div>
      <p className={cn(styles['ctm-success-message'])}>
        {tr(
          'FRONTEND.SUPPORT.CREATE.SUCCESS_MESSAGE',
          'Your support request has been submitted. Our team will get back to you shortly.'
        )}
      </p>
      {submittedId != null && <span className={cn(styles['ctm-success-ref'])}>TKT-{submittedId}</span>}
      <div className={cn(styles['ctm-success-actions'])}>
        <CoreButton
          variation='primary'
          size='large'
          fullWidth
          text={tr('FRONTEND.SUPPORT.CREATE.VIEW_TICKET', 'Go to your ticket')}
          onClick={() => submittedId != null && onSuccess(submittedId)}
        />
        <CoreButton
          variation='tertiary'
          size='large'
          fullWidth
          text={tr('FRONTEND.SUPPORT.CREATE.SUBMIT_ANOTHER', 'Submit another ticket')}
          onClick={resetForm}
        />
        <CoreButton
          variation='tertiary'
          size='large'
          fullWidth
          text={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
          onClick={handleSuccessClose}
        />
      </div>
    </div>
  )

  const formContent = (
    <div className={cn(styles['ctm-content'])}>
      <div className={cn(styles['ctm-body'])}>
        <div className={cn(styles['ctm-left'])}>
          <CoreInput
            type='text'
            name='title'
            label={tr('FRONTEND.SUPPORT.CREATE.TITLE_LABEL', 'Title')}
            required
            maxLength={25}
            value={title}
            onChange={(_name: string, value: string) => setTitle(value)}
            placeholder={tr('FRONTEND.SUPPORT.CREATE.TITLE_PLACEHOLDER', 'Brief description of your issue')}
          />

          <CoreSelect
            type='default'
            name='categoryId'
            label={tr('FRONTEND.SUPPORT.CREATE.CATEGORY_LABEL', 'Category')}
            required
            options={categoryOptions}
            value={categoryId === '' ? null : categoryId}
            onChange={(_name: string, value: number | string) => setCategoryId(value ? Number(value) : '')}
          />

          <p className={cn(styles['ctm-hint'])}>
            {tr(
              'FRONTEND.SUPPORT.CREATE.HINT',
              'Fill in the title and category, then compose your messages on the right before submitting.'
            )}
          </p>

          <ErrorDisplay error={submitError} />
        </div>

        <div
          className={cn(styles['ctm-right'], {
            [styles['drag-active']]: dragActive
          })}
          onDragOver={e => {
            e.preventDefault()
            setDragActive(true)
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={e => {
            e.preventDefault()
            setDragActive(false)
            addFiles(Array.from(e.dataTransfer.files))
          }}
        >
          <div className={cn(styles['ctm-messages-header'])}>
            <span className={cn(styles['ctm-messages-row-label'])}>
              {tr('FRONTEND.SUPPORT.CREATE.MESSAGES_LABEL', 'Initial Messages')}
            </span>
            <span className={cn(styles['ctm-messages-count'])}>
              ({committedMessageSlots}/{MAX_INITIAL_MESSAGES})
            </span>
            <span className={cn(styles['ctm-messages-hint'])}>
              {tr('FRONTEND.SUPPORT.CREATE.SHIFT_ENTER', 'Use Shift+Enter for new lines · Max 10 messages')}
            </span>
          </div>
          <div className={cn(styles['ctm-messages-header'], styles['ctm-messages-total-chars-row'])}>
            <span className={cn(styles['ctm-messages-row-label'])}>
              {tr('FRONTEND.SUPPORT.CREATE.TOTAL_CHARS_LABEL', 'Total Characters')}
            </span>
            <span className={cn(styles['ctm-messages-count'])}>
              ({totalMessageCharacters}/{MAX_TOTAL_MESSAGE_LENGTH})
            </span>
          </div>

          <div className={cn(styles['ctm-messages-area'])}>
            {sentMessages.length === 0 ? (
              <div className={cn(styles['ctm-empty-messages'])}>
                <svg width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                  <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
                </svg>
                <span>{tr('FRONTEND.SUPPORT.CREATE.EMPTY_MESSAGES', 'Type messages below or drop files here')}</span>
              </div>
            ) : (
              sentMessages.map((msg, i) => (
                <div key={`${msg.text}-${i}`} className={cn(styles['ctm-sent-message'])}>
                  {msg.text && <div className={cn('ctm-sent-text')}>{msg.text}</div>}
                  {msg.files.map(f => (
                    <span key={f.name} className={cn(styles['ctm-sent-file'])}>
                      📎 {f.name}
                    </span>
                  ))}
                </div>
              ))
            )}
          </div>

          <div className={cn(styles['ctm-compose'])}>
            <ComposeArea
              ref={composeRef}
              text={composeText}
              onTextChange={setComposeText}
              onKeyDown={handleComposeKeyDown}
              files={composeFiles}
              fileErrors={fileErrors}
              onDismissFileErrors={() => setFileErrors([])}
              onRemoveFile={removeComposeFile}
              onAddFiles={addFiles}
              placeholder={tr('FRONTEND.SUPPORT.CREATE.MESSAGE_PLACEHOLDER', 'Type a message...')}
              maxLength={MAX_MESSAGE_LENGTH}
              disabled={committedMessageSlots >= MAX_INITIAL_MESSAGES}
              textareaClassName={cn(styles['ctm-compose-textarea'])}
              inlineButtons={
                <>
                  <MMButton
                    type='button'
                    size='normal'
                    variant='tertiary'
                    collapsed
                    collapsedIcon={
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                        <path d='M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' />
                      </svg>
                    }
                    caption={tr('FRONTEND.SUPPORT.CREATE.ATTACH', 'Attach a file')}
                    onClick={() => composeRef.current?.openFilePicker()}
                    disabled={committedMessageSlots >= MAX_INITIAL_MESSAGES}
                    data-test='attach-file-button'
                  />
                  <MMButton
                    type='button'
                    size='normal'
                    variant='primary'
                    collapsed
                    collapsedIcon={
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                        <line x1='22' y1='2' x2='11' y2='13' />
                        <polygon points='22 2 15 22 11 13 2 9 22 2' />
                      </svg>
                    }
                    caption={tr('FRONTEND.SUPPORT.CREATE.ADD_MESSAGE', 'Add message')}
                    onClick={handleAddMessage}
                    disabled={!canAddMessage || committedMessageSlots >= MAX_INITIAL_MESSAGES}
                  />
                </>
              }
            />
            <span
              className={cn(styles['compose-char-count'], {
                [styles.warn]: composeText.length > MAX_MESSAGE_LENGTH * 0.9
              })}
            >
              {composeText.length}/{MAX_MESSAGE_LENGTH}
            </span>
          </div>
        </div>
      </div>

      <CoreModalDialogFooter className={cn(styles['ctm-footer'])}>
        <div className={cn(styles['ctm-footer-actions'])}>
          <MMButton
            type='button'
            size='normal'
            variant='tertiary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={handleClose}
            data-test='cancel-button'
          />
          <MMButton
            type='button'
            size='normal'
            variant='primary'
            caption={`✓ ${tr('FRONTEND.SUPPORT.CREATE.SUBMIT', 'Create Ticket')}`}
            onClick={handleSubmit}
            loading={createFetching}
            disabled={!canSubmit || createFetching}
            data-test='create-ticket-button'
          />
        </div>
      </CoreModalDialogFooter>
    </div>
  )

  return (
    <CoreModalDialog
      visible={visible}
      onClose={isSuccess ? handleSuccessClose : handleClose}
      options={{ size: isSuccess ? 'auto' : 'large', closeOnClickOutside: true }}
      header={
        isSuccess
          ? tr('FRONTEND.SUPPORT.CREATE.SUCCESS_TITLE', 'Request Submitted!')
          : tr('FRONTEND.SUPPORT.CREATE.TITLE', 'Create new ticket')
      }
      content={isSuccess ? successContent : formContent}
    />
  )
}

export default CreateTicketModal
